import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Check, ArrowRight } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ServiceDetailDialog from '@/components/ServiceDetailDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Services page — works for BOTH public visitors and logged-in clients.
 *
 * - Public: shows the marketing chrome (Header/Footer), each card opens the
 *   detail dialog in 'quote' mode → captures contact info → creates a draft
 *   quote in the admin queue.
 * - Logged-in: shows the dashboard chrome, dialog opens in 'subscribe' mode →
 *   directly creates a ServiceSubscription with the configured pricing.
 *
 * "Starting at" price logic mirrors the ServiceDetailDialog: tiered services
 * show the cheapest tier; per_device shows base price; flat shows the price.
 */
const Services = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [services, setServices] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeService, setActiveService] = useState(null);

  const fetchData = useCallback(async (loggedIn) => {
    try {
      const services_p = axios.get(`${BACKEND_URL}/api/services`);
      const subs_p = loggedIn
        ? axios.get(`${BACKEND_URL}/api/subscriptions`, { withCredentials: true }).catch(() => ({ data: [] }))
        : Promise.resolve({ data: [] });
      const [sRes, subRes] = await Promise.all([services_p, subs_p]);
      setServices(sRes.data);
      setSubscriptions(subRes.data);
    } catch (e) {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const boot = async () => {
      try {
        const me = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
        if (!cancelled) {
          setUser(me.data);
          setAuthChecked(true);
          fetchData(true);
        }
      } catch (e) {
        if (!cancelled) {
          setUser(null);
          setAuthChecked(true);
          fetchData(false);
        }
      }
    };
    boot();
    return () => { cancelled = true; };
  }, [fetchData]);

  const isSubscribed = (sid) =>
    subscriptions.some((s) => s.service_id === sid && s.status === 'active');

  const startingPrice = (svc) => {
    const pt = svc.pricing_type || 'flat';
    if (pt === 'tiered' && svc.tiers?.length) {
      return Math.min(...svc.tiers.map((t) => Number(t.price) || 0));
    }
    return Number(svc.price) || 0;
  };

  const billingLabel = (cycle) => ({
    monthly: '/mo',
    quarterly: '/qtr',
    yearly: '/yr',
    'one-time': 'one-time',
  })[cycle] || cycle;

  if (loading || !authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground font-mono">Loading...</p>
        </div>
      </div>
    );
  }

  const isLoggedIn = !!user;
  const dialogMode = isLoggedIn && user.role === 'client' ? 'subscribe' : 'quote';

  const ChromeHeader = isLoggedIn ? (
    <header className="sticky top-0 z-40 w-full border-b border-border backdrop-blur-xl bg-background/80">
      <div className="container mx-auto px-6 md:px-12">
        <div className="flex h-16 items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />Dashboard
          </Button>
          <div className="font-black text-xl uppercase tracking-tight">
            NEXT<span className="text-primary">/</span>GEN
          </div>
        </div>
      </div>
    </header>
  ) : (
    <Header />
  );

  return (
    <div className="min-h-screen bg-background">
      {ChromeHeader}

      <div className="container mx-auto px-6 md:px-12 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl sm:text-5xl font-black uppercase mb-2">
            {isLoggedIn ? 'AVAILABLE SERVICES' : 'OUR SERVICES'}
          </h1>
          <p className="text-muted-foreground mb-12">
            {isLoggedIn
              ? 'Click a service to configure pricing and subscribe.'
              : 'Click a service to see details, configure your plan, and request a quote.'}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const subscribed = isSubscribed(service.service_id);
              const fromPrice = startingPrice(service);
              const pt = service.pricing_type || 'flat';

              return (
                <motion.div
                  key={service.service_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.05 }}
                >
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setActiveService(service)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveService(service); } }}
                    className="text-left w-full group cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
                    data-testid={`service-card-${service.service_id}`}
                  >
                    <Card className="p-6 border-border group-hover:border-primary/60 transition-colors h-full flex flex-col cursor-pointer">
                      <div className="relative h-48 -mx-6 -mt-6 mb-6 overflow-hidden">
                        <img
                          src={service.image_url}
                          alt={service.name}
                          className="w-full h-full object-cover transition-transform group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-card via-card/50 to-transparent" />
                      </div>

                      <div className="flex-1">
                        <h3 className="text-2xl font-bold mb-2 group-hover:text-primary transition-colors">
                          {service.name}
                        </h3>
                        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{service.description}</p>

                        <div className="mb-4">
                          <div className="flex items-baseline gap-2">
                            {pt !== 'flat' && (
                              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                from
                              </span>
                            )}
                            <span className="text-3xl font-black text-primary font-mono">
                              ${fromPrice.toFixed(2)}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {billingLabel(service.billing_cycle)}
                            </span>
                          </div>
                          {pt === 'tiered' && (
                            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                              {service.tiers?.length || 0} plans available
                            </p>
                          )}
                          {pt === 'per_device' && (
                            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                              + ${(service.per_device_price || 0).toFixed(2)}/device
                            </p>
                          )}
                          {service.addons?.length > 0 && (
                            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                              {service.addons.length} add-on{service.addons.length === 1 ? '' : 's'} available
                            </p>
                          )}
                        </div>

                        <div className="space-y-1.5 mb-4">
                          {service.features.slice(0, 4).map((f, idx) => (
                            <div key={idx} className="flex items-start gap-2">
                              <Check className="w-3.5 h-3.5 text-primary mt-0.5 flex-shrink-0" />
                              <span className="text-xs text-muted-foreground">{f}</span>
                            </div>
                          ))}
                          {service.features.length > 4 && (
                            <p className="text-[10px] text-muted-foreground italic">
                              +{service.features.length - 4} more
                            </p>
                          )}
                        </div>
                      </div>

                      {subscribed ? (
                        <Badge className="w-full justify-center py-2 bg-primary/10 text-primary border border-primary/30">
                          <Check className="w-4 h-4 mr-2" />Subscribed
                        </Badge>
                      ) : (
                        <div className="w-full bg-primary text-primary-foreground font-bold uppercase py-2 px-4 rounded-md flex items-center justify-center group-hover:bg-primary/90 transition-colors">
                          {dialogMode === 'subscribe' ? 'Configure & Subscribe' : 'View Details & Quote'}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </div>
                      )}
                    </Card>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>

      {!isLoggedIn && <Footer />}

      <ServiceDetailDialog
        service={activeService}
        open={!!activeService}
        onClose={() => setActiveService(null)}
        mode={dialogMode}
        onSuccess={() => {
          if (isLoggedIn) fetchData(true);
        }}
      />
    </div>
  );
};

export default Services;
